'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface InstagramPost {
  id: string;
  caption: string;
  post_url: string;
  image?: string;
}

interface InstagramFeedProps {
  posts: InstagramPost[];
}

const InstagramCard: React.FC<{ post: InstagramPost; index: number }> = ({ post, index }) => {
  const gradients = [
    'from-purple-600 via-pink-500 to-orange-400',
    'from-pink-500 via-red-400 to-yellow-400',
    'from-violet-600 via-purple-500 to-pink-500',
    'from-indigo-500 via-purple-500 to-pink-400',
  ];
  const gradient = gradients[index % gradients.length];

  return (
    <a
      href={post.post_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative flex flex-col rounded-2xl overflow-hidden shadow-subtle hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 bg-card aspect-square"
    >
      {/* Photo or Gradient Placeholder */}
      {post.image ? (
        <AppImage
          src={post.image}
          alt={post.caption || 'Instagram post'}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} flex items-center justify-center`}>
          {/* Instagram logo SVG when no photo */}
          <svg viewBox="0 0 24 24" fill="white" className="w-16 h-16 opacity-60">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </div>
      )}

      {/* Persistent subtle top gradient for Instagram icon */}
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/40 to-transparent z-10 flex items-start justify-between p-3">
        {/* Instagram logo badge */}
        <div className="flex items-center gap-1.5">
          <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5 opacity-90">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </div>
        <div className="bg-white/20 backdrop-blur-sm rounded-full p-1">
          <Icon name="ArrowTopRightOnSquareIcon" size={13} className="text-white" />
        </div>
      </div>

      {/* Hover Overlay: dark transparent background + caption */}
      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 z-20">
        {post.caption && (
          <p className="text-white text-sm leading-relaxed line-clamp-5">
            {post.caption}
          </p>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-white/80 text-xs font-semibold">
          <span>View on Instagram</span>
          <Icon name="ArrowTopRightOnSquareIcon" size={13} />
        </div>
      </div>
    </a>
  );
};

const InstagramFeed: React.FC<InstagramFeedProps> = ({ posts }) => {
  return (
    <div className="bg-gradient-to-br from-accent/5 to-supporting/5 rounded-lg p-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-4">
          <Icon name="CameraIcon" size={20} />
          <span className="text-sm font-semibold">Follow Our Journey</span>
        </div>
        <h2 className="text-3xl font-heading font-bold text-foreground mb-3">
          Instagram Feed
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Stay connected with our daily impact stories and community moments on Instagram
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {posts.map((post, index) => (
          <InstagramCard key={post.id} post={post} index={index} />
        ))}
      </div>

      <div className="text-center">
        <a
          href="https://www.instagram.com/helping_charitable_trust/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg font-heading font-semibold hover:bg-accent/90 transition-all duration-300 shadow-subtle hover:shadow-cta"
        >
          <Icon name="CameraIcon" size={20} />
          <span>Follow @sevasetutrust</span>
        </a>
      </div>
    </div>
  );
};

export default InstagramFeed;